/* Master · Medicine Requests — the requester's side of the flow.
   An operator who cannot find a medicine in the master (typically mid-GRN or
   mid-bill-entry) raises a request here and tracks it until the master-data team
   decides it. Approving / rejecting happens on the sibling screen,
   MedicineRequestApprovals.jsx.

   Backend: POST/GET v1/medicine-requests. Only the medicine name and a contact
   number are mandatory — everything else helps the approver identify the product,
   so the form asks for it but never blocks on it. */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import supplyService from "../../Services/supplyService";
import {
  getCurrentUserMobile,
  getCurrentUserName,
  getResolvedWarehouseCode,
} from "../../Services/warehouseContext";
import {
  Button,
  Input,
  Select,
  SectionHeader,
  ToolbarBar,
  TabBar,
  LoadingState,
  ErrorState,
  InlineAlert,
  IconPlus,
  IconCheck,
  IconPackage,
  phoneError,
} from "../Supply/supplyKit";
import {
  Drawer,
  Field,
  RequestDetail,
  RequestPager,
  RequestsTable,
  StatusPill,
  fieldInput,
  useDebounced,
  useMedicineRequestCounts,
  useMedicineRequests,
} from "./medicineRequestKit";
import { WarehouseSelect } from "../Supply/warehousePicker";
import { useWarehouse } from "../Layout/WarehouseContext";

const TABS = [
  { id: "", label: "All" },
  { id: "PENDING", label: "Pending" },
  { id: "APPROVED", label: "Approved" },
  { id: "REJECTED", label: "Rejected" },
];

const MedicineRequests = () => {
  const [tab, setTab] = useState("");
  const [q, setQ] = useState("");
  const search = useDebounced(q);
  /* Scoped to the store selected app-wide (topbar) — the same scoping every other
     screen now uses, so a head switches store once rather than per screen. */
  const { code: warehouseId } = useWarehouse();
  const [viewing, setViewing] = useState(null);
  const [raising, setRaising] = useState(false);

  const query = useMedicineRequests({ status: tab, search, warehouseId });
  const [countsKey, setCountsKey] = useState(0);
  const counts = useMedicineRequestCounts({ search, warehouseId, reloadKey: countsKey });

  const refresh = () => {
    query.reload();
    setCountsKey((k) => k + 1);
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Medicine Requests"
        sub="Ask the master-data team to add a medicine that isn't in the master yet"
        actions={
          <Button variant="primary" icon={<IconPlus size={14} />} onClick={() => setRaising(true)}>
            Raise request
          </Button>
        }
      />

      <TabBar
        tabs={TABS.map((t) => ({
          ...t,
          count: t.id ? counts[t.id] : undefined,
          alert: t.id === "PENDING" && counts.PENDING > 0 ? "amber" : undefined,
        }))}
        value={tab}
        onChange={setTab}
      />

      <ToolbarBar>
        <Input
          placeholder="Search medicine name / request number…"
          style={{ width: 320, maxWidth: "100%" }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Button size="sm" onClick={refresh} disabled={query.loading}>
          Refresh
        </Button>
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {query.totalElements} request{query.totalElements !== 1 ? "s" : ""}
        </div>
      </ToolbarBar>

      {query.loading ? (
        <LoadingState label="Loading medicine requests…" />
      ) : query.error ? (
        <ErrorState message={query.error} onRetry={refresh} />
      ) : (
        <>
          <RequestsTable
            rows={query.rows}
            onOpen={setViewing}
            emptyHint={
              q || tab
                ? "No requests match this filter."
                : "No medicine requests yet. Raise one when a product is missing from the master."
            }
          />
          {query.totalElements > 0 && <RequestPager q={query} />}
        </>
      )}

      {viewing && <RequestViewDrawer req={viewing} onClose={() => setViewing(null)} />}

      {raising && (
        <RaiseRequestDrawer
          onClose={() => setRaising(false)}
          onSaved={() => {
            setRaising(false);
            refresh();
          }}
        />
      )}
    </div>
  );
};

/* ---------------- Read-only detail ----------------
   The requester can see the decision but never change it. */
const RequestViewDrawer = ({ req, onClose }) => (
  <Drawer
    icon={<IconPackage size={18} />}
    title={req.medicineName || "Medicine request"}
    sub={
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        {req.requestNumber || "—"}
        <StatusPill status={req.status} />
      </span>
    }
    onClose={onClose}
    footer={
      <>
        <Button onClick={onClose}>Close</Button>
        <div style={{ flex: 1 }} />
        {req.status === "PENDING" && (
          <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>Waiting on the master-data team</span>
        )}
        {req.status === "APPROVED" && req.medicineCode && (
          <span style={{ fontSize: 11, color: "var(--green-700)", fontFamily: "var(--font-mono)" }}>
            Use code {req.medicineCode}
          </span>
        )}
      </>
    }
  >
    <RequestDetail req={req} />
  </Drawer>
);

/* ---------------- Raise a request ---------------- */
const blankRequest = () => ({
  medicineName: "",
  manufacturer: "",
  composition: "",
  packing: "",
  unitPack: "",
  hsnCode: "",
  gstPercentage: "",
  mrp: "",
  purchasePrice: "",
  invoiceNumber: "",
  invoiceDate: "",
  supplierName: "",
  supplierId: "",
  warehouseId: getResolvedWarehouseCode() || "",
  remarks: "",
  /* Who to call back, prefilled from the signed-in user — the backend rejects a
     request with no requesterNumber, and both fields are editable for the case
     where someone else should be contacted. getCurrentUserName()'s "system"
     fallback is not a person, so it is not offered as a name. */
  requesterName: getCurrentUserName() === "system" ? "" : getCurrentUserName(),
  requesterNumber: getCurrentUserMobile(),
});

const RaiseRequestDrawer = ({ onClose, onSaved }) => {
  const [form, setForm] = useState(blankRequest);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const setF = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  /* Supplier list so the request can name a real supplier id rather than free
     text — scoped to the warehouse the request is for. */
  useEffect(() => {
    let alive = true;
    supplyService
      .getSuppliers({ warehouseId: form.warehouseId || undefined })
      .then((list) => {
        if (alive) setSuppliers((list || []).filter((s) => s.isActive !== false));
      })
      .catch(() => {
        if (alive) setSuppliers([]);
      });
    return () => {
      alive = false;
    };
  }, [form.warehouseId]);

  const phoneErr = phoneError(form.requesterNumber);
  const nameOk = form.medicineName.trim().length > 0;
  const numberOk = form.requesterNumber.trim().length > 0 && !phoneErr;
  /* HSN stays optional, but a typed one must be well-formed: an approved request
     becomes master data, and a bad HSN there later prefills GRN lines. Same rule
     as the GRN screen and IMS. */
  const hsnErr = form.hsnCode.trim() !== "" && !/^(\d{6}|\d{8})$/.test(form.hsnCode.trim());
  const valid = nameOk && numberOk && !hsnErr;

  /* Picking a supplier fills the name too — the backend stores both, and the
     approver reads the name. */
  const chooseSupplier = (id) => {
    const hit = suppliers.find((s) => String(s.id) === String(id));
    setForm((f) => ({ ...f, supplierId: id, supplierName: hit ? hit.name : f.supplierName }));
  };

  const handleSave = () => {
    if (!valid) return;
    setSaving(true);
    setErr(null);
    supplyService
      .createMedicineRequest(form)
      .then((saved) => {
        toast.success(
          `Request raised${saved?.requestNumber ? ` — ${saved.requestNumber}` : ""}. The master-data team has been notified.`
        );
        onSaved?.(saved);
      })
      .catch((e) => {
        setSaving(false);
        setErr(e?.response?.data?.message || e?.message || "Could not raise the request.");
      });
  };

  return (
    <Drawer
      icon={<IconPackage size={18} />}
      title="Raise medicine request"
      sub="Notifies the master-data team by email and WhatsApp"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <div style={{ flex: 1 }} />
          <span
            style={{
              fontSize: 11,
              color: valid ? "var(--green-700)" : "var(--fg-muted)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {valid
              ? "Ready to send"
              : !nameOk
              ? "Medicine name required"
              : !numberOk
              ? phoneErr || "Contact number required"
              : "HSN must be 6 or 8 digits"}
          </span>
          <Button
            variant="primary"
            icon={<IconCheck size={14} />}
            disabled={!valid || saving}
            onClick={handleSave}
          >
            {saving ? "Sending…" : "Send request"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
        <Field label="Medicine name *" wide>
          <input
            value={form.medicineName}
            onChange={(e) => setF("medicineName", e.target.value)}
            maxLength={255}
            placeholder="Dolo 650 Tablet"
            style={fieldInput}
          />
        </Field>
        <Field label="Manufacturer">
          <input
            value={form.manufacturer}
            onChange={(e) => setF("manufacturer", e.target.value)}
            maxLength={255}
            placeholder="Micro Labs Ltd"
            style={fieldInput}
          />
        </Field>
        <Field label="Composition">
          <input
            value={form.composition}
            onChange={(e) => setF("composition", e.target.value)}
            maxLength={1000}
            placeholder="Paracetamol 650mg"
            style={fieldInput}
          />
        </Field>
        <Field label="Packing">
          <input
            value={form.packing}
            onChange={(e) => setF("packing", e.target.value)}
            maxLength={100}
            placeholder="1X15"
            style={fieldInput}
          />
        </Field>
        <Field label="Units / pack">
          <input
            value={form.unitPack}
            onChange={(e) => setF("unitPack", e.target.value.replace(/[^\d]/g, ""))}
            inputMode="numeric"
            placeholder="15"
            style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
          />
        </Field>
        <Field label="HSN code">
          <input
            value={form.hsnCode}
            onChange={(e) => setF("hsnCode", e.target.value.replace(/[^\d]/g, ""))}
            inputMode="numeric"
            maxLength={8}
            placeholder="6 or 8 digits"
            title="HSN code — 6 or 8 digits"
            style={{
              ...fieldInput,
              fontFamily: "var(--font-mono)",
              border: hsnErr ? "1px solid var(--red-500)" : fieldInput.border,
            }}
          />
        </Field>
        <Field label="GST %">
          <Select
            value={form.gstPercentage}
            onChange={(e) => setF("gstPercentage", e.target.value)}
            style={{ ...fieldInput, height: 30, cursor: "pointer" }}
          >
            <option value="">—</option>
            {[0, 5, 12, 18, 28].map((g) => (
              <option key={g} value={g}>
                {g}%
              </option>
            ))}
          </Select>
        </Field>
        <Field label="MRP">
          <input
            value={form.mrp}
            onChange={(e) => setF("mrp", e.target.value.replace(/[^\d.]/g, ""))}
            inputMode="decimal"
            placeholder="30.80"
            style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
          />
        </Field>
        <Field label="Purchase price">
          <input
            value={form.purchasePrice}
            onChange={(e) => setF("purchasePrice", e.target.value.replace(/[^\d.]/g, ""))}
            inputMode="decimal"
            placeholder="22.50"
            style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
          />
        </Field>

        <div style={{ gridColumn: "1 / -1", borderTop: "1px solid var(--border-subtle)", paddingTop: 4 }} />

        <Field label="Warehouse">
          {/* Defaults to the signed-in user's warehouse; only a pharmacy head
              can raise a request on behalf of another one. */}
          <WarehouseSelect
            value={form.warehouseId}
            onChange={(v) => setF("warehouseId", v)}
            warehouses={warehouses}
            noneLabel="— Select warehouse —"
            /* Requests are raised for the store you are working in, chosen once
               in the topbar. */
            disabled
            width="100%"
          />
        </Field>
        <Field label="Supplier">
          <Select
            value={form.supplierId}
            onChange={(e) => chooseSupplier(e.target.value)}
            style={{ ...fieldInput, height: 30, cursor: "pointer" }}
          >
            <option value="">— Not from a supplier invoice —</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Invoice number">
          <input
            value={form.invoiceNumber}
            onChange={(e) => setF("invoiceNumber", e.target.value)}
            maxLength={100}
            placeholder="INV-2026-0451"
            style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
          />
        </Field>
        <Field label="Invoice date">
          <input
            type="date"
            value={form.invoiceDate}
            onChange={(e) => setF("invoiceDate", e.target.value)}
            style={{ ...fieldInput, fontFamily: "var(--font-mono)" }}
          />
        </Field>

        <div style={{ gridColumn: "1 / -1", borderTop: "1px solid var(--border-subtle)", paddingTop: 4 }} />

        <Field label="Your name">
          <input
            value={form.requesterName}
            onChange={(e) => setF("requesterName", e.target.value)}
            maxLength={255}
            placeholder="Ramesh Kumar"
            style={fieldInput}
          />
        </Field>
        <Field
          label="Your mobile *"
          hint={
            getCurrentUserMobile()
              ? "From your profile. The decision comes back to this number on WhatsApp."
              : "The decision comes back to this number on WhatsApp."
          }
        >
          <input
            value={form.requesterNumber}
            onChange={(e) => setF("requesterNumber", e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
            inputMode="numeric"
            maxLength={10}
            placeholder="9876543210"
            style={{
              ...fieldInput,
              fontFamily: "var(--font-mono)",
              border: phoneErr ? "1px solid var(--red-500)" : fieldInput.border,
            }}
          />
          {phoneErr && <div style={{ fontSize: 10, color: "var(--red-700)", marginTop: 4 }}>{phoneErr}</div>}
        </Field>
        <Field label="Remarks" wide>
          <textarea
            value={form.remarks}
            onChange={(e) => setF("remarks", e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Not found in master during GRN"
            style={{ ...fieldInput, height: "auto", padding: 8, resize: "vertical" }}
          />
        </Field>
      </div>

      {err && (
        <div style={{ marginTop: 12 }}>
          <InlineAlert>{err}</InlineAlert>
        </div>
      )}
    </Drawer>
  );
};

export default MedicineRequests;
